/**
 * Product Support Team Inquiry Screen
 *
 * Shared component for submitting clinical questions to Pharma Product Support experts.
 * Used by both open (unauthenticated) and secure (authenticated) routes.
 * Uses two-column layout on desktop with product sidebar, single column on mobile.
 *
 * Auth behavior:
 * - Logged-out users see an AuthCard at the top with disabled form fields
 * - Logged-in users needing NPI verification see a blocking NPI overlay (web only)
 */

import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { InquirySubmitSuccessScreen } from '@/components/MedicalOnDemand/InquirySubmitSuccessScreen';
import { ProductSupportGuidelines } from '@/components/MedicalOnDemand/ProductSupportGuidelines';
import { ProductSupportSidebar } from '@/components/MedicalOnDemand/ProductSupportSidebar';
import { ComplianceCheckbox } from '@/components/MedicalOnDemand/form/ComplianceCheckbox';
import { ProductSelector } from '@/components/MedicalOnDemand/form/ProductSelector';
import { ProductTeamQuestionField } from '@/components/MedicalOnDemand/form/ProductTeamQuestionField';
import { QuestionTypeSelect } from '@/components/MedicalOnDemand/form/QuestionTypeSelect';
import { RouteField } from '@/components/MedicalOnDemand/form/RouteField';
import { RouteZipCodeField } from '@/components/MedicalOnDemand/form/RouteZipCodeField';
import { ProductSupportMobileHeader } from '@/components/MedicalOnDemand/mobile/ProductSupportMobileHeader';
import { ProductTeamDetailsSheet } from '@/components/MedicalOnDemand/mobile/ProductTeamDetailsSheet';
import { AuthCard } from '@/components/authCard';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import {
  getProductData,
  getProductSupportTeam,
  getQuestionTypePlaceholder,
  TREATMENT_SUPPORT_LABEL,
  type Product,
  type ProductId,
} from '@/constants/medicalOnDemand';
import { ROUTES } from '@/constants/routes';
import { needsVerificationToSubmitInquiry, useAbility } from '@/hooks/useAbility';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useColors } from '@/hooks/useColors';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMODInquiryForm } from '@/hooks/useMODInquiryForm';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useModTeamName } from '@/hooks/useModTeamName';
import { useProductSupportSuccessEntry } from '@/hooks/useProductSupportSuccessEntry';
import { useToaster } from '@/hooks/useToaster';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { productSupportPrefillMessageAtom, userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { Sentry } from '@/util/sentry';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-xl' });

export default function ProductSupportTeamInquiry() {
  const router = useRouter();

  const { teamId, prefillProductId, prefillSource } = useLocalSearchParams<{
    teamId: string;
    prefillProductId?: string;
    prefillSource?: string;
  }>();

  const prefillMessage = useAtomValue(productSupportPrefillMessageAtom);

  const setPrefillMessage = useSetAtom(productSupportPrefillMessageAtom);

  const { bottom } = useSafeAreaInsets();

  const { colors } = useColors();

  const toast = useToaster();

  const [showConfirmation, setShowConfirmation] = useState(false);

  const [showGuidelines, setShowGuidelines] = useState(false);

  const [showAbout, setShowAbout] = useState(false);

  const [focusedProductId, setFocusedProductId] = useState<ProductId | null>(null);

  const { requestAccess } = useAbilityGate();

  const userId = useAtomValue(userIdAtom);

  const isLoggedIn = !!userId;

  const formDisabled = !userId;

  const currentUserQuery = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const ability = useAbility();

  const needsNpiVerification =
    isLoggedIn && !currentUserQuery.isLoading && needsVerificationToSubmitInquiry(ability);

  const isSubmitDenied =
    isLoggedIn && !currentUserQuery.isLoading && !ability.can('submit', 'Inquiry');

  const isModProductFeatured = useFeatureFlag(FEATURE_FLAGS.MOD_PRODUCT_FEATURED);

  const isModDirectToMslEnabled = useFeatureFlag(FEATURE_FLAGS.MOD_DIRECT_TO_MSL);

  const isMaintenanceMode = useMaintenanceMode();

  const {
    control,
    errors,
    formValues,
    setValue,
    submitInquiry,
    isSubmitting,
    submissionStatus,
    canSubmit,
  } = useMODInquiryForm({
    defaultPostalCode: currentUserQuery.data?.postalCode,
    onSuccess: () => {
      segmentClient
        .track(ANALYTICS_EVENTS.MOD_SUBMISSION_SUCCEEDED, {
          source: productSupportSource,
          prefillProductId: initialPrefillProductId,
          selectedProductId: formValues.productIds?.[0] ?? null,
          product_ids: formValues.productIds || null,
          product_count: formValues.productIds?.length ?? 0,
          questionType: formValues.questionType || null,
          routes: formValues.routes ?? null,
          route_count: formValues.routes?.length ?? 0,
          direct_to_msl: formValues.routes?.includes('msl') ?? false,
        })
        ?.catch(() => {});

      setPrefillMessage('');

      setShowConfirmation(true);
    },
    requireQuestionType: isModProductFeatured,
  });

  const productIdsForUi = (useWatch({
    control,
    name: 'productIds',
    defaultValue: [] as ProductId[],
  }) ?? []) as ProductId[];

  const isMilEnabled = useFeatureFlag(FEATURE_FLAGS.MOD_MIL_ROUTE);

  const pharmaProductSupportTeam = teamId ? getProductSupportTeam(teamId) : undefined;

  const teamName = useModTeamName(pharmaProductSupportTeam?.name);

  const selectedProducts = productIdsForUi
    .map((id) => getProductData(id))
    .filter((p): p is Product => !!p);

  const focusedProduct = focusedProductId ? getProductData(focusedProductId) : null;

  const selectedProduct = focusedProduct ?? selectedProducts[selectedProducts.length - 1] ?? null;

  useEffect(() => {
    if (productIdsForUi.length === 0) {
      setFocusedProductId(null);

      return;
    }

    setFocusedProductId((currentFocusedProductId) => {
      if (currentFocusedProductId && productIdsForUi.includes(currentFocusedProductId)) {
        return currentFocusedProductId;
      }

      return productIdsForUi[productIdsForUi.length - 1];
    });
  }, [productIdsForUi]);

  const { source: productSupportSource, initialPrefillProductId } = useProductSupportSuccessEntry({
    pharmaProductSupportTeam,
    prefillProductId,
    prefillMessage,
    prefillSource,
    currentProductIds: productIdsForUi,
    setValue,
  });

  const hasReportedMissingTeam = useRef(false);

  useEffect(() => {
    if (pharmaProductSupportTeam || hasReportedMissingTeam.current) {
      return;
    }

    hasReportedMissingTeam.current = true;

    toast.showError({
      title: `${TREATMENT_SUPPORT_LABEL} team not available`,
      message: `${TREATMENT_SUPPORT_LABEL} team is not currently available. Please try again later.`,
    });

    Sentry.captureException(new Error('Product support team not available'), {
      extra: { teamId },
    });

    router.replace(ROUTES.DIRECTORY);
  }, [teamId, pharmaProductSupportTeam, toast, router]);

  if (!pharmaProductSupportTeam) {
    return null;
  }

  /** Built once for two possible positions, so the branches cannot drift. */
  const routeSection = (
    <>
      <RouteField control={control} errors={errors} isDisabled={formDisabled || isSubmitting} />

      <RouteZipCodeField
        control={control}
        errors={errors}
        isDisabled={formDisabled || isSubmitting}
      />
    </>
  );

  const handleDismiss = () => {
    router.replace(ROUTES.DIRECTORY);
  };

  const handleOpenGuidelines = () => {
    segmentClient.track(ANALYTICS_EVENTS.MOD_GUIDELINES_VIEWED)?.catch(() => {});

    setShowGuidelines(true);
  };

  const handleOpenAbout = () => {
    setShowAbout(true);
  };

  if (showConfirmation) {
    return <InquirySubmitSuccessScreen onDismiss={handleDismiss} />;
  }

  const formContent = (
    <VStack className="flex-2">
      <VStack className="md:hidden">
        <ProductSupportMobileHeader
          onOpenGuidelines={handleOpenGuidelines}
          onOpenAbout={handleOpenAbout}
          pharmaProductSupportTeam={pharmaProductSupportTeam}
          selectedProductIds={productIdsForUi}
        />
      </VStack>

      <Card variant="filled" className="mb-6 hidden bg-background-info md:flex">
        <VStack space="sm">
          <Heading size="md" className="text-typography-900">
            Ask {teamName}
          </Heading>
          <Text className="text-sm text-typography-700">{pharmaProductSupportTeam.guidance}</Text>
          <LinkText size="sm" className="text-primary-500" onPress={handleOpenGuidelines}>
            View Guidelines
          </LinkText>
        </VStack>
      </Card>

      {isWeb && formDisabled && (
        <AuthCard className="mb-6" text="Sign in or create an account to submit an inquiry" />
      )}

      <VStack className="mb-6">
        <ProductSelector
          products={pharmaProductSupportTeam.supportedProducts}
          control={control}
          errors={errors}
          isDisabled={formDisabled || isSubmitting}
        />
      </VStack>

      <VStack space="lg">
        {/*
          Above the question once the flag is on: the route decides who reads
          the message, which is worth knowing before writing it, and it matches
          every other inquiry form here. It moves with the flag rather than
          ahead of it so that turning the flag off restores the live screen.
        */}
        {isModDirectToMslEnabled && isMilEnabled ? routeSection : null}

        {isModProductFeatured && (
          <QuestionTypeSelect
            control={control}
            errors={errors}
            isDisabled={formDisabled || isSubmitting}
          />
        )}

        <ProductTeamQuestionField
          control={control}
          errors={errors}
          isDisabled={formDisabled || isSubmitting}
          placeholder={getQuestionTypePlaceholder(formValues.questionType || '')}
        />

        {isModDirectToMslEnabled && !isMilEnabled ? (
          <>
            {routeSection}

            <Divider className="mb-8 mt-6 bg-outline-200" />
          </>
        ) : null}

        <ComplianceCheckbox
          control={control}
          errors={errors}
          isDisabled={formDisabled || isSubmitting}
        />

        <Button
          onPress={submitInquiry}
          isDisabled={
            isMaintenanceMode || formDisabled || isSubmitDenied || !canSubmit() || isSubmitting
          }
          className={twJoin(
            'gap-x-2 px-20 web:w-fit',
            (isMaintenanceMode || formDisabled || isSubmitDenied) && 'opacity-50'
          )}>
          {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}
          <ButtonText>{isSubmitting ? submissionStatus || 'Submitting...' : 'Submit'}</ButtonText>
        </Button>
      </VStack>
    </VStack>
  );

  const twoColumnContent = (
    <HStack className="md:gap-x-10">
      <VStack className="w-full py-5 md:w-1/2 md:pt-0">{formContent}</VStack>

      <VStack className="hidden py-5 md:flex md:w-1/2 md:pt-0">
        <ProductSupportSidebar
          pharmaProductSupportTeam={pharmaProductSupportTeam}
          selectedProductIds={productIdsForUi}
          focusedProductId={focusedProductId}
          onFocusedProductChange={setFocusedProductId}
        />
      </VStack>
    </HStack>
  );

  return (
    <VStack className="flex-1 bg-background-0">
      <VStack className="mx-auto w-full max-w-screen-xl px-5 md:px-10">
        <Header
          backRoute={ROUTES.DIRECTORY}
          contentScrollsUnderHeader
          title="New Inquiry"
          statusBarBackgroundColor={colors.background[0]}
          isBackButton
        />
      </VStack>

      <VStack className={twJoin('flex-1', SCREEN_COLUMN.frame)} style={{ paddingBottom: bottom }}>
        {isWeb ? (
          <ScrollView
            className="flex-1"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled">
            {twoColumnContent}
          </ScrollView>
        ) : (
          <KeyboardAwareScrollView
            mode="layout"
            contentInsetAdjustmentBehavior="automatic"
            bottomOffset={18}
            className="flex-1"
            contentContainerClassName={SCREEN_COLUMN.content}
            showsVerticalScrollIndicator={false}
            keyboardDismissMode="interactive">
            {twoColumnContent}
          </KeyboardAwareScrollView>
        )}
      </VStack>

      <ProductSupportGuidelines isOpen={showGuidelines} onClose={() => setShowGuidelines(false)} />
      <ProductTeamDetailsSheet
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        pharmaProductSupportTeam={pharmaProductSupportTeam}
        selectedProduct={selectedProduct}
        selectedProductIds={productIdsForUi}
      />

      {isWeb && needsNpiVerification ? (
        <View
          className="absolute left-0 top-0 h-full w-full"
          onStartShouldSetResponder={() => {
            requestAccess();

            return false;
          }}
        />
      ) : null}
    </VStack>
  );
}
